import {
  DatasetFormat,
  DatasetStorageMode,
  AssetStatus,
  TestAssetType,
  type Dataset,
  type DatasetBinding,
  type DatasetRow,
} from '@k-ucap/shared';
import type { CsvParserService } from './CsvParserService.js';

export interface DatasetImportOptions {
  name: string;
  description?: string;
  projectId: string;
  createdBy: string;
  format?: DatasetFormat;
  storageMode?: DatasetStorageMode;
}

export interface DatasetService {
  importFromCsv(content: string, options: DatasetImportOptions): Dataset;
  importFromJson(rows: unknown[], options: DatasetImportOptions): Dataset;
  suggestBindings(dataset: Dataset, fieldKeys: string[]): DatasetBinding[];
  getRows(dataset: Dataset): DatasetRow[];
}

const normalizeRows = (rows: unknown[]): DatasetRow[] => {
  return rows
    .filter((row): row is Record<string, unknown> => row !== null && typeof row === 'object')
    .map((row) => ({ ...row }));
};

const normalizeColumns = (rows: DatasetRow[]): string[] => {
  const columns = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      columns.add(key);
    }
  }
  return Array.from(columns);
};

const normalizeName = (name: string): string => name.trim().toLowerCase();

const similarityScore = (a: string, b: string): number => {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (na === nb) return 1;
  if (na.includes(nb) || nb.includes(na)) return 0.7;
  return 0;
};

export const createDatasetService = (csvParser: CsvParserService): DatasetService => {
  const importFromCsv = (content: string, options: DatasetImportOptions): Dataset => {
    const parsed = csvParser.parse(content);
    const rows = parsed.rows;
    const now = new Date().toISOString();
    const id = `dataset-${Date.now()}`;
    return {
      id,
      projectId: options.projectId,
      assetType: TestAssetType.DATASET,
      name: options.name,
      description: options.description ?? '',
      format: options.format ?? DatasetFormat.CSV,
      columns: parsed.columns,
      rowCount: parsed.rowCount,
      storageMode: options.storageMode ?? DatasetStorageMode.INLINE,
      previewRows: rows.slice(0, 50),
      rows,
      bindings: [],
      tags: [],
      version: '1.0',
      revision: 1,
      status: AssetStatus.ACTIVE,
      createdBy: options.createdBy,
      createdAt: now,
      updatedAt: now,
      metadata: {
        parseErrors: parsed.errors,
      },
    };
  };

  const importFromJson = (rows: unknown[], options: DatasetImportOptions): Dataset => {
    const normalizedRows = normalizeRows(rows);
    const columns = normalizeColumns(normalizedRows);
    const now = new Date().toISOString();
    const id = `dataset-${Date.now()}`;
    return {
      id,
      projectId: options.projectId,
      assetType: TestAssetType.DATASET,
      name: options.name,
      description: options.description ?? '',
      format: options.format ?? DatasetFormat.JSON,
      columns,
      rowCount: normalizedRows.length,
      storageMode: options.storageMode ?? DatasetStorageMode.INLINE,
      previewRows: normalizedRows.slice(0, 50),
      rows: normalizedRows,
      bindings: [],
      tags: [],
      version: '1.0',
      revision: 1,
      status: AssetStatus.ACTIVE,
      createdBy: options.createdBy,
      createdAt: now,
      updatedAt: now,
      metadata: {},
    };
  };

  const suggestBindings = (dataset: Dataset, fieldKeys: string[]): DatasetBinding[] => {
    const bindings: DatasetBinding[] = [];
    const usedColumns = new Set<string>();
    for (const fieldKey of fieldKeys) {
      let bestColumn: string | undefined;
      let bestScore = 0.3;
      for (const column of dataset.columns) {
        if (usedColumns.has(column)) continue;
        const score = similarityScore(column, fieldKey);
        if (score > bestScore) {
          bestScore = score;
          bestColumn = column;
        }
      }
      if (bestColumn) {
        bindings.push({
          datasetId: dataset.id,
          column: bestColumn,
          fieldKey,
          required: false,
        });
        usedColumns.add(bestColumn);
      }
    }
    return bindings;
  };

  const getRows = (dataset: Dataset): DatasetRow[] => {
    return dataset.rows ?? dataset.previewRows ?? [];
  };

  return { importFromCsv, importFromJson, suggestBindings, getRows };
};
